import {
    filterSelectableSuggestions,
    filterCombinedSelectableSuggestions
} from './attributes-helpers';

const initialState = {
    // The attributes for the current category
    categoryAttributes: [],
    // The attributes of the draft.
    /**
     draftAttributes: {
      termAttributeValues: [
        {
          id: 1,
          backingField: 'clothes_size',
          userGenerated: true,
          values: [
            'b'
          ]
        }
      ],
      numberAttributeValues: []
    }
     */
    draftAttributes: {},
    // A list of the selected attributes (draftAttributes) for display on the page.
    // Exists for ease of use in views.
    // Format:
    /**
     {
         title: 'Klädstorlek',
         value: 'L'
     }
     */
    selectableSuggestedAttributes: [],
    selectableCategorySuggestedAttributes: [],
    selectableImageToColorSuggestedAttributes: [],
    // Attribute suggestions from the image-to-color service.
    imageToColorSuggestedAttributes: [],
    validationErrors: {}
};

const setCategoryAttributesSuggestions = (state, action) => {
    const { payload } = action;
    const newState = {
        ...state,
        categorySuggestedAttributes: payload.suggestedAttributes,
        selectableCategorySuggestedAttributes: filterSelectableSuggestions(
            payload.suggestedAttributes,
            state.categoryAttributes
        ),
        suggestionsExtractorType: payload.suggestionsExtractorType
    };
    updateSelectableSuggestedAttributes(newState);
    return newState;
};

const setImageToColorSuggestions = (state, action) => {
    const { payload } = action;
    const newState = {
        ...state,
        imageToColorSuggestedAttributes: payload.suggestedAttributes
    };
    updateSelectableImageToColorSuggestedAttributes(newState);
    return newState;
};

const updateSelectableSuggestedAttributes = newState => {
    // eslint-disable-next-line better-mutation/no-mutation
    newState.selectableSuggestedAttributes = filterCombinedSelectableSuggestions(
        newState.selectableCategorySuggestedAttributes,
        newState.selectableImageToColorSuggestedAttributes,
        newState.categoryAttributes
    );
};

const updateSelectableImageToColorSuggestedAttributes = newState => {
    // eslint-disable-next-line better-mutation/no-mutation
    newState.selectableImageToColorSuggestedAttributes = filterSelectableSuggestions(
        newState.imageToColorSuggestedAttributes,
        newState.categoryAttributes
    );
    updateSelectableSuggestedAttributes(newState);
};

const setCategoryAttributeOption = (state, action) => {
    const { payload } = action;
    const attribute = state.categoryAttributes.find(
        a => a.id === payload.attributeId
    );
    let options = attribute.options;
    if (
        !attribute.isMultiSelect ||
        (!payload.optionValue && !payload.activate)
    ) {
        options = options.map(o =>
            !o.isActive ? o : { ...o, isActive: false }
        );
    }
    if (payload.optionValue) {
        options = options.map(o =>
            o.value !== payload.optionValue
                ? o
                : { ...o, isActive: payload.activate }
        );
    }
    const numberOfValuesSelected = options.filter(o => o.isActive).length;
    return {
        ...state,
        categoryAttributes: state.categoryAttributes.map(a =>
            a.id !== payload.attributeId
                ? a
                : { ...attribute, options, numberOfValuesSelected }
        )
    };
};

const updateCategoryAttributesOptionsStatuses = state => {
    const categoryAttributes = state.categoryAttributes.map(a => {
        const draftAttributeValues =
            state.draftAttributes.termAttributeValues?.find(
                tav => a.id === tav.id
            )?.values || [];
        const options = a.options.map(o => {
            const isActive = draftAttributeValues.includes(o.value);
            return o.isActive === isActive ? o : { ...o, isActive };
        });
        const numberOfValuesSelected = options.filter(o => o.isActive).length;
        return { ...a, options, numberOfValuesSelected };
    });
    return { ...state, categoryAttributes };
};

const setDraftAttributes = (state, action) => {
    const newState = {
        ...state,
        draftAttributes: {
            ...action.payload
        }
    };
    return newState;
};

const setCategoryAttributes = (state, action) => {
    const newState = {
        ...state,
        categoryAttributes: [...action.payload]
    };
    updateSelectableImageToColorSuggestedAttributes(newState);
    return newState;
};

const resetAttributes = state => {
    return {
        ...initialState,
        // Resetting image-to-color suggested attributes is done separately
        imageToColorSuggestedAttributes: state.imageToColorSuggestedAttributes,
        selectableImageToColorSuggestedAttributes:
            state.selectableImageToColorSuggestedAttributes
    };
};

const resetSelectedAttributes = state => {
    return updateCategoryAttributesOptionsStatuses({
        ...state,
        draftAttributes: {}
    });
};

const resetImageAttributeSuggestions = state => {
    const newState = {
        ...state,
        imageToColorSuggestedAttributes:
            initialState.imageToColorSuggestedAttributes
    };
    updateSelectableImageToColorSuggestedAttributes(newState);
    return newState;
};

const validate = state => {
    const validationErrors = {};
    state.categoryAttributes.forEach(attribute => {
        if (attribute.minNumberOfValues === 0) {
            return;
        }
        if (attribute.options.length === 0) {
            // There were no options to choose from.
            // May not happen when backend implementation is done,
            // we should not get an empty list of options.
            return;
        }
        const values = attribute.options.reduce((acc, option) => {
            if (option.isActive) {
                acc.push(option.value);
            }
            return acc;
        }, []);
        if (values.length === 0) {
            // eslint-disable-next-line better-mutation/no-mutation
            validationErrors[
                attribute.backingField
            ] = `attributes:::syi_validation_missing_attribute_${attribute.backingField}`;
        }
    });

    return { ...state, validationErrors };
};

const reducers = {
    RESET_ATTRIBUTES: resetAttributes,
    RESET_SELECTED_ATTRIBUTES: resetSelectedAttributes,
    RESET_IMAGE_ATTRIBUTES_SUGGESTIONS: resetImageAttributeSuggestions,
    SET_CATEGORY_ATTRIBUTES: setCategoryAttributes,
    SET_DRAFT_ATTRIBUTES: setDraftAttributes,
    SET_CATEGORY_ATTRIBUTE_OPTION: setCategoryAttributeOption,
    UPDATE_CATEGORY_ATTRIBUTES_OPTIONS_STATUSES: updateCategoryAttributesOptionsStatuses,
    SET_CATEGORY_ATTRIBUTES_SUGGESTIONS: setCategoryAttributesSuggestions,
    SET_IMAGE_ATTRIBUTES_SUGGESTIONS: setImageToColorSuggestions
};

export default (state = initialState, action) => {
    const reducer = reducers[action.type];
    if (reducer) {
        return validate(reducer(state, action));
    }
    return state;
};
